import React, { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
	AccountBlockingException,
	AuthorizationException,
	CouldNotCreateEntitlementException,
	EntitlementExpiredException,
	Exception,
	GeoBlockingException,
	NotPlayableException,
	PurchaseLimitReached,
	UnsupportedDeviceException,
} from '@vodafoneis/sjonvarpskjarni-js-lib';
import { PlaybackContext } from '../../contexts/PlaybackContext';
import PlaybackStartingTitle from './PlaybackStartingTitle';
import PlaybackStartingSubtitle from './PlaybackStartingSubtitle';

function useExceptionTranslation(exception: Exception) {
	const { t } = useTranslation();

	if (exception instanceof AccountBlockingException) {
		return {
			title: t('PlaybackStarting.Error.AccountBlocking.Title'),
			subtitle: t('PlaybackStarting.Error.AccountBlocking.Subtitle'),
		};
	}

	if (exception instanceof AuthorizationException) {
		return {
			title: t('PlaybackStarting.Error.Authorization.Title'),
			subtitle: t('PlaybackStarting.Error.Authorization.Subtitle'),
		};
	}

	if (exception instanceof CouldNotCreateEntitlementException) {
		return {
			title: t('PlaybackStarting.Error.CouldNotCreateEntitlement.Title'),
			subtitle: t('PlaybackStarting.Error.CouldNotCreateEntitlement.Subtitle'),
		};
	}

	if (exception instanceof EntitlementExpiredException) {
		return {
			subtitle: t('PlaybackStarting.Error.EntitlementExpired.Subtitle'),
		};
	}

	if (exception instanceof GeoBlockingException) {
		return {
			title: t('PlaybackStarting.Error.GeoBlocking.Title'),
			subtitle: t('PlaybackStarting.Error.GeoBlocking.Subtitle'),
		};
	}

	if (exception instanceof NotPlayableException) {
		return {
			title: t('PlaybackStarting.Error.NotPlayable.Title'),
			subtitle: t('PlaybackStarting.Error.NotPlayable.Subtitle'),
		};
	}

	if (exception instanceof UnsupportedDeviceException) {
		return {
			title: t('PlaybackStarting.Error.UnsupportedDevice.Title'),
			subtitle: t('PlaybackStarting.Error.UnsupportedDevice.Subtitle'),
		};
	}

	if (exception instanceof PurchaseLimitReached) {
		return {
			title: t('PlaybackStarting.Error.PurchaseLimitReached.Title'),
			subtitle: t('PlaybackStarting.Error.PurchaseLimitReached.Subtitle'),
		};
	}

	return {
		title: t('PlaybackStarting.Error.Default.Title'),
		subtitle: t('PlaybackStarting.Error.Default.Subtitle'),
	};
}

export default function PlaybackStartingError() {
	const { playlistItem, exception } = useContext(PlaybackContext);

	const { title, subtitle } = useExceptionTranslation(exception);

	const formattedTitle = useMemo(() => {
		if (title) return title;

		return playlistItem?.getContentTitle();
	}, [playlistItem, title]);

	return (
		<div>
			<PlaybackStartingTitle>{formattedTitle}</PlaybackStartingTitle>
			<PlaybackStartingSubtitle>{subtitle}</PlaybackStartingSubtitle>
		</div>
	);
}
